/**
 * shared/doc.ts — THE document model. LAW 4.
 *
 * There is exactly one of these files, it is owned by ingest, and it is imported by the client, the
 * server and the worker. There is exactly one segmenter (shared/text/segment.ts), it runs only at
 * ingest, and its output is a stored row.
 *
 * Why that is a law and not a preference: books.karti.ai shipped fragment-id divergence. The narrator
 * and the EPUB exporter each decided independently what counted as a fragment; when they differed by
 * one block the overlay highlighted the wrong words and NOTHING ERRORED ANYWHERE. The book read one
 * step out of sync and the listener assumed they had misread. `"the U.S. Army moved."` is one
 * sentence under one splitter and two under another — one clip too many, one cue that fails to
 * resolve, a desync guard that drops the highlight with a reason nobody reads.
 *
 * So: the old sentence-splitter identifier appears nowhere in this repository — CI greps for it, and a
 * hit is a bug report. If you need sentences, you read the `sentences` spans that ingest wrote.
 *
 * Nothing here does I/O and nothing here imports a `node:` builtin — the client bundles this file, and
 * one filesystem import in shared/ breaks the Vite build with an error that points at the wrong file.
 */

// Type-only, and circular with access.ts on purpose: `AccessBook` is the four fields this file owns,
// and `ShareGrantKind` is the vocabulary that file owns. Both erase at compile time, so the cycle
// never reaches the bundler.
import type { ShareGrantKind } from "./access.ts";
import type { NarrationStyle } from "./narration/pacing.ts";

// ─── the block ───────────────────────────────────────────────────────────────

export type BlockKind =
  | "heading" | "para" | "verse" | "quote" | "list-item"
  | "caption" | "image" | "code" | "table" | "rule" | "note";

export type RunStyle = "em" | "strong" | "code" | "link" | "smallcaps" | "sup" | "sub" | "noteref";

/** A character range in `Block.text` carrying inline style. Half-open: [start, end). */
export interface Run { start: number; end: number; style: RunStyle; href?: string }

/** A sentence's character range in `Block.text`. Half-open: [start, end). `id` is the sid. */
export interface Span { id: string; ord: number; start: number; end: number }

export interface Block {
  /** "c003.b0042" — LOCAL to the edition. LAW 1: carry `editionId` beside it in every row and message. */
  id: string;
  chapterOrd: number;
  ord: number;
  kind: BlockKind;
  /** heading 1-6 */
  level?: number;
  /**
   * PLAIN, NFC, whitespace-collapsed. The canonical string, and the ONLY one.
   * It is the hashing surface, the TTS surface and the highlight surface at once: one string, three
   * consumers, zero re-derivation. Styling is character ranges (`runs`) rather than nested markup so
   * that "the intersection of a bold run and a spoken sentence" is arithmetic on two integer ranges
   * instead of a tree walk.
   *
   * Footnote markers never appear here — `epub:type="noteref"` becomes a zero-width Run and the text
   * moves to `notes`. That is why `text` never contains "[3]" and why the segmenter never has to cope
   * with one (§3.1).
   */
  text: string;
  runs: Run[];
  /**
   * STORED at ingest. NEVER recomputed — not by the client at render time, not by the worker "to
   * check". This field is the whole of LAW 4 expressed as data: the reader's <span> boundaries, the
   * narrator's clip boundaries and the cue list's sids are the same objects because they are all read
   * from this array rather than each derived from `text`.
   *
   * Consequence for anyone editing verse: a stanza break is a real `rule` BLOCK, never an empty span.
   * books stored verse as a parallel array with "" holes and a `filter(Boolean)` silently misaligned
   * every poem from that point on, forever. There is no array here to compact — keep it that way.
   */
  sentences: Span[];
  speak: boolean;
  /**
   * BCP-47. REQUIRED, on every block, with no default filled in downstream.
   * Safari silently refuses to hyphenate an element with no resolvable language, and a 34-character
   * phone column without hyphenation is unreadable. Highest-value single attribute in the reader.
   */
  lang: string;
  image?: { sha: string; w: number; h: number; alt: string };
  /** "OEBPS/ch03.xhtml#p17" | "pdf:p41,b3" — provenance, including the print folio for citation. */
  srcRef?: string;
  /** Unicode script tag, recorded not guessed. A non-Latin block gets one span rather than a guess (§3.2). */
  script?: string;
  /** OCR mean word confidence, when applicable. The gate exists; nothing produces it in v1 (§3.7). */
  conf?: number;
}

// ─── the sentence row ────────────────────────────────────────────────────────

/**
 * One row of the `sentences` table. `id` is the sid and is the atom of the entire product.
 * `ttsText` and `ttsTextSha` are voice-INDEPENDENT and denormalised so the "does this page need
 * GPU?" planner is one indexed join rather than 20,000 hashes at query time (§4.3). The per-voice
 * clip keys live in `sentence_clips`, never here — one column cannot hold the key for N voices.
 */
export interface Sentence {
  /** "c003.b0042.s2" */
  id: string;
  blockId: string;
  chapterOrd: number;
  blockOrd: number;
  sentOrd: number;
  /** Dense 0-based index across the whole edition. The `ord` projection (§2.1). */
  globalOrd: number;
  /** Half-open range into the parent block's `text`. */
  charStart: number;
  charEnd: number;
  text: string;
  ttsText: string;
  ttsTextSha: string;
  /** sha256(norm(prev) ‖ norm(self) ‖ norm(next))[0:16] — a matching HINT for re-import, not a key (§3.6). */
  anchor: string;
  selfKey: string;
  speak: boolean;
  /** Position on the book's linear narration axis. Null until a page containing it has been stitched. */
  startMs: number | null;
  durMs: number | null;
  /** True while `durMs` is `chars / SPEECH_CHARS_PER_SEC` rather than a measured clip length. */
  durEstimated: boolean;
}

/** A footnote or endnote, lifted out of `Block.text` at ingest so no marker is ever spoken. */
export interface Note {
  id: string;
  blockId: string;
  marker: string | null;
  text: string;
  runs: Run[];
}

// ─── chapters, pages, editions, books ────────────────────────────────────────

export type ChapterRole = "front" | "body" | "back";

export interface Chapter {
  /** "c003" */
  id: string;
  /** Source order. Chapter IDS NEVER RENUMBER — the sid depends on this number and editions are immutable. */
  ord: number;
  /** Reading order. This is the one the structure editor moves; `ord` stays put. */
  readOrd: number;
  title: string;
  role: ChapterRole;
  speak: boolean;
  charCount: number;
  /**
   * Hard split points as block indices, computed at ingest when the chapter exceeds 60k chars (§5.1).
   * The reader mounts one part at a time so `scrollWidth` never forces layout over a 400-page track.
   */
  parts: number[];
  srcRef?: string;
}

/**
 * A CANONICAL page: a server-side deterministic run of blocks (PAGE_TARGET_CHARS = 1500), snapped to
 * block ends, never crossing a chapter, never splitting a verse block, never ending on a heading.
 * It is the generation, caching, prefetch and delivery unit, and it has a row, an audio artifact and
 * a cue list.
 *
 * It is NOT what the reader shows. A DISPLAY page is a client-computed column, recomputed on every
 * resize and font change, and never stored. The two relate only through sentence ids and do not need
 * to align: one audio file may span two display pages on a phone and sit inside one on a MacBook, and
 * both are right, because the page turn is driven by columnOf(sid) and never by a file boundary.
 */
export interface Page {
  /** "c003.p07" */
  id: string;
  ord: number;
  chapterOrd: number;
  /** Inclusive range over `Sentence.globalOrd`. */
  startOrd: number;
  endOrd: number;
  startSid: string;
  endSid: string;
  charCount: number;
  role: ChapterRole;
}

export type EditionStructure = "linear" | "canon" | "collection";
export type EditionState = "ingesting" | "ready" | "failed";

/**
 * One ingest of one source file by one toolchain version. NEVER MUTATED IN PLACE.
 *
 * Re-importing a corrected file, upgrading the parser, or changing segmentation produces a NEW
 * edition; the old one stays live until nothing references it. That immutability is exactly what
 * makes positional addressing safe, and it is load-bearing — see LAW 1 and LAW 2.
 */
export interface Edition {
  /** "ed_" + 12B base36 */
  id: string;
  bookId: string;
  sourceSha: string;
  sourceExt: string;
  parserVersion: number;
  segmenterVersion: number;
  cleanerVersion: number;
  /** sha of `pdftotext -v` + `ffmpeg -version`. An apt upgrade that changes segmentation must not be invisible. */
  toolVersions: string;
  structure: EditionStructure;
  lang: string;
  /**
   * Where the book begins. ONE column, TWO consumers: it drives both GET /books/:id/start (reading)
   * and generation priority. Two columns would drift and nobody would know which was wrong — and
   * half-wiring this is precisely how the previous R&D failed, narrating the copyright page first.
   */
  bodyStartSid: string | null;
  bodyStartConfidence: number | null;
  bodyStartReason: string | null;
  /** Null means unconfirmed: blocks BULK generation, never the interactive one-page ensure (LAW 6). */
  structureConfirmedAt: number | null;
  needsReview: boolean;
  sentenceCount: number;
  charCount: number;
  state: EditionState;
  createdAt: number;
}

export type BookVisibility = "private" | "unlisted" | "public";
export type BookKind = "prose" | "poetry" | "canon" | "manual";
export type NarrationPolicy = "page" | "chapter" | "book";

export interface BookMeta {
  /**
   * "bk_" + 16B base36 (~83 bits). For an UNLISTED book the id IS the capability, which is why
   * `slug` is set only when visibility === "public" — a pretty guessable slug on an unlisted book
   * makes "unlisted" mean nothing.
   */
  id: string;
  ownerId: string;
  title: string;
  author: string | null;
  slug: string | null;
  visibility: BookVisibility;
  /** Set when a user asks for `public`; the book stays unlisted until an admin flips it (§6.3). */
  publicRequestedAt: number | null;
  currentEditionId: string | null;
  kind: BookKind;
  narrationStyle: NarrationStyle;
  /** Default "page", deliberately. Nothing may raise a book above one-page granularity on its own initiative. */
  narrationPolicy: NarrationPolicy;
  narrateAltText: boolean;
  coverSha: string | null;
  /** Takedown OUTRANKS ownership — see canSee() in shared/access.ts. */
  takenDownAt: number | null;
  deletedAt: number | null;
  createdAt: number;
  /**
   * P4 (§6.4). What a **live** share link held by the caller of *this* request confers on this book,
   * carried straight through from `readAccess`'s per-lookup resolution.
   *
   * It is on `BookMeta` and not on the session payload because a grant is a fact about a *pair* —
   * this reader, this book — and this is the object that already crosses the wire per book. It is
   * what makes `canRegenerate(book, actor)` in `shared/access.ts` give the same answer in the reader's
   * sentence menu as the server gives at `POST /sentences/:sid/regenerate` (LAW 13). Absent for
   * everybody not holding a link, which is almost everybody.
   */
  grants?: readonly ShareGrantKind[];
  /** A live named-account read grant resolved for this caller. It never implies edit access. */
  directAccess?: boolean;
}

/** Canonical page target. Snapped to block ends; see `Page`. */
export const PAGE_TARGET_CHARS = 1500;

/** Above this, a chapter is split into `Chapter.parts` so the reader never lays out the whole track (§5.1). */
export const CHAPTER_PART_MAX_CHARS = 60_000;
